import React from 'react'

interface MarkdownRendererProps {
  content: string
}

/**
 * Parses inline formatting: **bold** and `code`
 */
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Split by bold (**...**) or code (`...`)
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g
  const tokens = text.split(regex)

  tokens.forEach((token, idx) => {
    if (!token) return
    if (token.startsWith('**') && token.endsWith('**')) {
      parts.push(
        <strong key={idx} className="font-semibold text-[var(--color-text)]">
          {token.slice(2, -2)}
        </strong>,
      )
    } else if (token.startsWith('`') && token.endsWith('`')) {
      parts.push(
        <code
          key={idx}
          className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11.5px] text-slate-800 border border-slate-200 break-all inline-block max-w-full"
        >
          {token.slice(1, -1)}
        </code>,
      )
    } else {
      parts.push(token)
    }
  })

  return parts
}

/**
 * Clean, lightweight, XSS-safe Markdown and Table renderer for ERP AI Assistant.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  if (!content) return null

  const lines = content.split('\n')
  const blocks: React.ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Detect Fenced Code Block: starts with ```
    if (line.trim().startsWith('```')) {
      const codeLines: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      if (i < lines.length && lines[i].trim().startsWith('```')) {
        i++ // skip closing ```
      }
      blocks.push(
        <div
          key={`codeblock-${i}`}
          className="my-2.5 w-full max-w-full overflow-x-auto rounded-lg bg-slate-900 p-3 text-slate-100 shadow-2xs font-mono text-xs border border-slate-800"
        >
          <pre className="break-all whitespace-pre-wrap font-mono leading-relaxed">{codeLines.join('\n')}</pre>
        </div>,
      )
      continue
    }

    // Detect Markdown Table: starts with '|' and contains '|'
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim())
        i++
      }

      if (tableLines.length >= 2) {
        const headerRow = tableLines[0]
          .slice(1, -1)
          .split('|')
          .map((c) => c.trim())

        // Skip separator line (e.g. |---|---|)
        const dataRows = tableLines
          .slice(2)
          .map((r) =>
            r
              .slice(1, -1)
              .split('|')
              .map((c) => c.trim()),
          )

        blocks.push(
          <div
            key={`table-${i}`}
            className="my-3 w-full max-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-2xs [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100"
          >
            <table className="w-full min-w-full border-collapse text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-100/90 text-slate-700 border-b border-slate-200">
                  {headerRow.map((col, colIdx) => {
                    const colLower = col.toLowerCase()
                    const isStt = colLower === 'stt'
                    const isNumber =
                      /^\d+(\.\d+)?$/.test(col) ||
                      colLower.includes('số lượng') ||
                      colLower.includes('giá') ||
                      colLower.includes('tồn')
                    const isCompact = isStt || colLower === 'đvt' || colLower === 'dvt'

                    let alignClass = 'text-left'
                    if (isNumber) alignClass = 'text-right'
                    else if (isCompact) alignClass = 'text-center'

                    return (
                      <th
                        key={colIdx}
                        className={`px-2.5 py-2 font-semibold tracking-tight whitespace-nowrap border-r border-slate-200 last:border-r-0 ${alignClass}`}
                      >
                        {col}
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dataRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-slate-50/80 transition-colors">
                    {row.map((cell, cellIdx) => {
                      const colLower = headerRow[cellIdx]?.toLowerCase() || ''
                      const isStt = colLower === 'stt'
                      const isCompact = isStt || colLower === 'đvt' || colLower === 'dvt'
                      const isNumber =
                        /^\d+([.,]\d+)?(\s*[A-Za-zĐđ]+)?$/.test(cell.trim()) ||
                        colLower.includes('số lượng') ||
                        colLower.includes('giá') ||
                        colLower.includes('tồn')
                      const isTextCol =
                        colLower.includes('tên') ||
                        colLower.includes('mô tả') ||
                        colLower.includes('ghi chú')

                      let alignClass = 'text-left'
                      if (isNumber && !isStt) alignClass = 'text-right tabular-nums'
                      else if (isCompact) alignClass = 'text-center'

                      return (
                        <td
                          key={cellIdx}
                          className={`px-2.5 py-1.5 text-slate-800 border-r border-slate-100 last:border-r-0 ${alignClass} ${
                            isTextCol ? 'min-w-[120px] whitespace-normal break-words' : 'whitespace-nowrap'
                          }`}
                        >
                          {renderInline(cell)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        )
        continue
      }
    }

    // Bullet list item
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      const listItems: string[] = []
      while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))) {
        listItems.push(lines[i].trim().substring(2))
        i++
      }
      blocks.push(
        <ul key={`ul-${i}`} className="my-1.5 space-y-1 pl-4 text-xs sm:text-sm text-slate-800">
          {listItems.map((item, itemIdx) => (
            <li key={itemIdx} className="list-disc pl-1 leading-snug">
              {renderInline(item)}
            </li>
          ))}
        </ul>,
      )
      continue
    }

    // Numbered list item
    if (/^\d+\.\s/.test(line.trim())) {
      const listItems: string[] = []
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        listItems.push(lines[i].trim().replace(/^\d+\.\s*/, ''))
        i++
      }
      blocks.push(
        <ol key={`ol-${i}`} className="my-1.5 space-y-1 pl-5 text-xs sm:text-sm text-slate-800">
          {listItems.map((item, itemIdx) => (
            <li key={itemIdx} className="list-decimal pl-1 leading-snug">
              {renderInline(item)}
            </li>
          ))}
        </ol>,
      )
      continue
    }

    // Blockquote / Notice
    if (line.trim().startsWith('> ')) {
      blocks.push(
        <blockquote
          key={`quote-${i}`}
          className="my-2 border-l-3 border-[var(--color-primary)] bg-[var(--color-primary-soft)]/50 px-3 py-1.5 text-xs text-slate-700 italic rounded-r"
        >
          {renderInline(line.trim().substring(2))}
        </blockquote>,
      )
      i++
      continue
    }

    // Empty lines
    if (!line.trim()) {
      i++
      continue
    }

    // Normal paragraph
    blocks.push(
      <p key={`p-${i}`} className="mb-2 leading-relaxed text-xs sm:text-sm text-slate-800 last:mb-0 break-words">
        {renderInline(line)}
      </p>,
    )
    i++
  }

  return <div className="space-y-1 w-full max-w-full min-w-0">{blocks}</div>
}
